import { useGetPricing } from '@/api/gen/pricing/pricing.gen';
import { useGetUserPlanAvailability } from '@/api/gen/quiz-plan-payment/quiz-plan-payment.gen';
import { useUser } from '@/lib/auth';
import { useCountryCode } from '@/shared/hooks/useCountryCode';

/**
 * 첫 구매 할인 자격 판정 훅
 *
 * 로그인 유저: GET /quiz-plans/availability의 firstPurchasePromotion 기반
 * 비로그인 유저: GET /pricing의 activePromotions 기반
 *
 * 소비하는 쪽은 데이터 소스를 신경 쓸 필요 없이 isEligible, discountRate만 사용
 */
export function useFirstPurchaseDiscount() {
  const { isAuthenticated, user } = useUser();
  const { countryCode } = useCountryCode();
  const isFreeUser = user?.planType === 'FREE';

  // 소스 1: 로그인 유저 → availability API
  const { data: availabilityData } = useGetUserPlanAvailability({
    query: {
      enabled: isAuthenticated && isFreeUser,
      staleTime: Infinity,
      retry: 0,
      throwOnError: false,
      meta: { ignoreError: true },
    },
  });

  // 소스 2: 비로그인 → pricing API (이미 Pricing 페이지에서 캐시됨)
  const { data: pricingData } = useGetPricing(
    { countryCode },
    {
      query: {
        enabled: !isAuthenticated,
        staleTime: Infinity,
      },
    },
  );

  if (isAuthenticated) {
    // 로그인: 서버가 구매 이력 기반으로 판정
    const promotion = availabilityData?.data?.firstPurchasePromotion ?? null;
    const discountRate = promotion?.discountRate ?? 0;
    return {
      isEligible: promotion != null && !!discountRate,
      discountRate,
      targetPlanType: promotion?.targetPlanType ?? null,
    };
  }

  // 비로그인: 공개 프로모션 정보 기반
  const activePromotions = pricingData?.data?.activePromotions ?? [];
  const activePromotion =
    activePromotions.length > 0 ? activePromotions[0] : null;
  const discountRate = activePromotion?.discountRate ?? 0;

  return {
    isEligible: activePromotion != null && !!discountRate,
    discountRate,
    targetPlanType: activePromotion?.targetPlanTypes?.[0] ?? null,
  };
}
